import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import axios from "axios";
import "../../../../om/css/salesorder.css";
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { Splitter, Item as SplitterItem } from 'devextreme-react/splitter';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import ItemFilters from './itemTab/ItemFilters';
import ItemResult from './itemTab/ItemResult';
import LocationResult from './locationTab/LocationResult';
import LotFilters from './lotTab/LotFilters';
import LotResult from './lotTab/LotResult';
import SubLotFilters from './sublotTab/SubLotFilters';
import SubLotResult from './sublotTab/SubLotResult';
import ByLocationFilters from './byLocationTab/ByLocationFilters';
import ByLocationResult from './byLocationTab/ByLocationResult';
import LocationInventoryResult from './byLocationTab/LocationInventoryResult';
import EmptyLocationResult from './emptyLocationTab/EmptyLocationResult';
import EmptyLocationFilters from './emptyLocationTab/EmptyLocationFilters';
import { FillFilters } from '../../../../common/Utils';

// IM Item catalog endpoint (ItemController.GetItems -> DataSourceLoader over ItemDto).
const GetItemsUrl = "im/Item/GetItems";
// Location quantity summary per selected item/lot/sublot.
const GetLocationSummaryUrl = "im/LocationQuantitySummary/GetLocationQuantitySummary";
// Lot/Item inventory status (itemId empty -> all lots).
const GetLotInventoryUrl = "im/Lot/GetLotItemInventoryStatus";
// Sublot listing (SublotListDto, keyed by RowId).
const GetSubLotsUrl = "wm/SubLot/GetSublotListingData";
// Location listing (copied from Location Data Entry) + per-location inventory.
const GetLocationsUrl = "im/Location/GetLocations";
const GetLocationInventoryUrl = "im/LocationQuantitySummary/GetLocationInventory";
const GetEmptyLocationsUrl = "im/LocationQuantitySummary/GetEmptyLocations";
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

// Build a DevExtreme filter expression from the Lot search fields (applied server-side via loadOptions).
const buildLotFilters = (formFields) => {
    const map = [
        ["CustomerName", formFields.CustomerName],
        ["LotCode", formFields.LotName],
        ["LotClassCode", formFields.ClassCode],
        ["LotSubclassCode", formFields.SubClassCode],
        ["Description1", formFields.LotDescription1],
        ["Description2", formFields.LotDescription2],
    ];
    const parts = map
        .filter(([, value]) => value)
        .map(([column, value]) => [column, "contains", value]);
    if (parts.length === 0) {
        return null;
    }
    return parts.reduce((acc, part, index) => (index === 0 ? [part] : [...acc, "and", part]), []);
};

// Build a DevExtreme filter expression from the Sublot search fields (server-side via loadOptions).
// Columns map to SublotListDto (returned by GetSublotListingData).
const buildSubLotFilters = (formFields) => {
    const map = [
        ["CustomerName", formFields.CustomerName],
        ["SublotCode", formFields.SubLotName],
        ["Description1", formFields.Description1],
        ["Description2", formFields.Description2],
        ["Department", formFields.DepartmentName],
        ["ConsigneeName", formFields.ConsigneeName],
    ];
    const parts = map
        .filter(([, value]) => value)
        .map(([column, value]) => [column, "contains", value]);
    if (parts.length === 0) {
        return null;
    }
    return parts.reduce((acc, part, index) => (index === 0 ? [part] : [...acc, "and", part]), []);
};

// Build a DevExtreme filter expression from the Empty Locations search fields (server-side via
// loadOptions). Columns map to EmptyLocationDTO (returned by GetEmptyLocations).
const buildEmptyLocationFilters = (formFields) => {
    const map = [
        ["WarehouseIdentityID", formFields.Warehouse],
        ["LocationCode", formFields.Location],
        ["ClassCode", formFields.ClassCode],
        ["Subclasscode", formFields.SubClassCode],
        ["Description", formFields.Description],
    ];
    const parts = map
        .filter(([, value]) => value)
        .map(([column, value]) => [column, "contains", value]);
    if (parts.length === 0) {
        return null;
    }
    return parts.reduce((acc, part, index) => (index === 0 ? [part] : [...acc, "and", part]), []);
};

// Build a DevExtreme filter expression from the search form fields. Each filter field maps to
// its ItemDto column (PascalCase); empty fields are skipped and the rest are AND-ed together.
const buildItemFilters = (formFields) => {
    const conditions = [];
    if (formFields.CustomerName) {
        conditions.push(["CustomerName", "contains", formFields.CustomerName]);
    }
    // "Item Name" matches either the item code or its description (Description-1).
    if (formFields.ItemName) {
        conditions.push([
            ["ItemCode", "contains", formFields.ItemName],
            "or",
            ["Description1", "contains", formFields.ItemName],
        ]);
    }
    if (formFields.ClassCode) {
        conditions.push(["ClassCode", "contains", formFields.ClassCode]);
    }
    if (formFields.SubClassCode) {
        conditions.push(["SubClassCode", "contains", formFields.SubClassCode]);
    }
    if (formFields.ItemDescription1) {
        conditions.push(["Description1", "contains", formFields.ItemDescription1]);
    }
    if (formFields.ItemDescription2) {
        conditions.push(["Description2", "contains", formFields.ItemDescription2]);
    }
    if (conditions.length === 0) {
        return null;
    }
    return conditions.reduce((acc, part, index) => (index === 0 ? [part] : [...acc, "and", part]), []);
};

const LocationManagement = (props) => {
    const accessToken = props.oidc.user.access_token;
    const [itemData, setItemData] = useState(null);
    const [locationData, setLocationData] = useState([]);
    const [locationLoading, setLocationLoading] = useState(false);
    const [lotData, setLotData] = useState(null);
    const [lotLocationData, setLotLocationData] = useState([]);
    const [lotLocationLoading, setLotLocationLoading] = useState(false);
    const [subLotData, setSubLotData] = useState(null);
    const [subLotLocationData, setSubLotLocationData] = useState([]);
    const [subLotLocationLoading, setSubLotLocationLoading] = useState(false);
    const [byLocationData, setByLocationData] = useState(null);
    const [locationInventoryData, setLocationInventoryData] = useState(null);
    const [emptyLocationData, setEmptyLocationData] = useState(null);

    const searchData = (formFields) => {
        const store = createStore({
            key: 'RowId',
            loadUrl: `${window._env_.REACT_APP_APIS}/${GetItemsUrl}`,
            loadParams: {
                IsTransactionManagement: true,
            },
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const dataSource = new DataSource({ store });
        const filters = buildItemFilters(formFields || {});
        if (filters) {
            dataSource.filter(filters);
        }
        setItemData(dataSource);
        setLocationData([]);
    };

    // Load the full item catalog on first render.
    useEffect(() => {
        searchData({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load lots from the backend (server-side). Search re-hits the endpoint with the filter applied.
    const searchLotData = (formFields) => {
        const store = createStore({
            key: 'RowId',
            loadUrl: `${window._env_.REACT_APP_APIS}/${GetLotInventoryUrl}`,
            loadParams: {
                itemId: EMPTY_GUID,
            },
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const dataSource = new DataSource({ store });
        const filters = buildLotFilters(formFields || {});
        if (filters) {
            dataSource.filter(filters);
        }
        setLotData(dataSource);
        setLotLocationData([]);
    };

    // Load all lots on first render.
    useEffect(() => {
        searchLotData({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the location quantity summary for the selected lot (specific lot, all items).
    const onLotSelected = (lot) => {
        if (!lot || !lot.RowId) {
            setLotLocationData([]);
            return;
        }
        setLotLocationLoading(true);
        axios.get(`${window._env_.REACT_APP_APIS}/${GetLocationSummaryUrl}`, {
            params: { itemId: EMPTY_GUID, lotId: lot.RowId },
            headers: {
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((res) => {
            setLotLocationData(Array.isArray(res.data) ? res.data : []);
        }).catch(() => {
            setLotLocationData([]);
        }).finally(() => {
            setLotLocationLoading(false);
        });
    };

    // Load sublots from the backend (server-side). Search re-hits the endpoint with the filter applied.
    const searchSubLotData = (formFields) => {
        const store = createStore({
            key: 'RowId',
            loadUrl: `${window._env_.REACT_APP_APIS}/${GetSubLotsUrl}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const dataSource = new DataSource({ store });
        const filters = buildSubLotFilters(formFields || {});
        if (filters) {
            dataSource.filter(filters);
        }
        setSubLotData(dataSource);
        setSubLotLocationData([]);
    };

    // Load all sublots on first render.
    useEffect(() => {
        searchSubLotData({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the location quantity summary for the selected sublot.
    const onSubLotSelected = (subLot) => {
        if (!subLot || !subLot.RowId) {
            setSubLotLocationData([]);
            return;
        }
        setSubLotLocationLoading(true);
        axios.get(`${window._env_.REACT_APP_APIS}/${GetLocationSummaryUrl}`, {
            params: { itemId: EMPTY_GUID, lotId: EMPTY_GUID, sublotId: subLot.RowId },
            headers: {
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((res) => {
            setSubLotLocationData(Array.isArray(res.data) ? res.data : []);
        }).catch(() => {
            setSubLotLocationData([]);
        }).finally(() => {
            setSubLotLocationLoading(false);
        });
    };

    // Load locations from the backend (server-side), copied from Location Data Entry (GetLocations).
    const searchByLocationData = (formFields) => {
        const fields = formFields || {};
        const store = createStore({
            key: 'RowId',
            loadParams: fields,
            loadUrl: `${window._env_.REACT_APP_APIS}/${GetLocationsUrl}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const dataSource = new DataSource({ store });
        const filters = FillFilters(fields);
        if (filters && filters.length > 0) {
            dataSource.filter(filters);
        }
        setByLocationData(dataSource);
        setLocationInventoryData(null);
    };

    // Load all locations on first render.
    useEffect(() => {
        searchByLocationData({ Module: 'WM' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the empty-locations grid (server-side). Search re-hits the endpoint with the filter applied.
    const searchEmptyLocationData = (formFields) => {
        const store = createStore({
            key: 'RowId',
            loadUrl: `${window._env_.REACT_APP_APIS}/${GetEmptyLocationsUrl}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const dataSource = new DataSource({ store });
        const filters = buildEmptyLocationFilters(formFields || {});
        if (filters) {
            dataSource.filter(filters);
        }
        setEmptyLocationData(dataSource);
    };

    // Load all empty locations on first render.
    useEffect(() => {
        searchEmptyLocationData({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load the per-location inventory for the selected location (GetLocationInventory).
    const onLocationSelected = (location) => {
        if (!location || !location.RowId) {
            setLocationInventoryData(null);
            return;
        }
        const store = createStore({
            key: 'RowId',
            loadParams: { locationId: location.RowId },
            loadUrl: `${window._env_.REACT_APP_APIS}/${GetLocationInventoryUrl}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        setLocationInventoryData(new DataSource({ store }));
    };

    // Load the location quantity summary for the selected item (all lots).
    const onItemSelected = (item) => {
        if (!item || !item.RowId) {
            setLocationData([]);
            return;
        }
        setLocationLoading(true);
        axios.get(`${window._env_.REACT_APP_APIS}/${GetLocationSummaryUrl}`, {
            params: { itemId: item.RowId, lotId: EMPTY_GUID },
            headers: {
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((res) => {
            setLocationData(Array.isArray(res.data) ? res.data : []);
        }).catch(() => {
            setLocationData([]);
        }).finally(() => {
            setLocationLoading(false);
        });
    };

    return (
        <div className="wm-tab-container">
            <TabPanel>
                <TabItem title="By Item" style={{ paddingTop: '4px !important' }}>
                    <div className="row">
                        <div className="col-md-12 main-page">
                            <div id="accordion">
                                <ItemFilters searchData={searchData} />
                                {/* Resizable vertical splitter: top grid over the Locations detail tab. */}
                                <Splitter orientation="vertical" className="locationMgmtSplitter">
                                    <SplitterItem resizable={true} collapsible={true} minSize="120px" size="50%">
                                        <div className="locationMgmtSplitterPane">
                                            <ItemResult data={itemData} onItemSelected={onItemSelected} />
                                        </div>
                                    </SplitterItem>
                                    <SplitterItem resizable={true} collapsible={true} minSize="80px">
                                        <div className="locationMgmtSplitterPane">
                                            <TabPanel height="100%">
                                                <TabItem title="Locations" style={{ paddingTop: '4px !important' }}>
                                                    {/* Reserve the tab-header height so the grid + its pager fit inside the pane. */}
                                                    <div style={{ height: 'calc(48% - 42px)' }}>
                                                        <LocationResult data={locationData} loading={locationLoading} />
                                                    </div>
                                                </TabItem>
                                            </TabPanel>
                                        </div>
                                    </SplitterItem>
                                </Splitter>
                            </div>
                        </div>
                    </div>
                </TabItem>
                <TabItem title="By Lot" style={{ paddingTop: '4px !important' }}>
                    <div className="row">
                        <div className="col-md-12 main-page">
                            <div id="accordion">
                                <LotFilters searchData={searchLotData} />
                                <Splitter orientation="vertical" className="locationMgmtSplitter">
                                    <SplitterItem resizable={true} collapsible={true} minSize="120px" size="50%">
                                        <div className="locationMgmtSplitterPane">
                                            <LotResult data={lotData} onLotSelected={onLotSelected} />
                                        </div>
                                    </SplitterItem>
                                    <SplitterItem resizable={true} collapsible={true} minSize="80px">
                                        <div className="locationMgmtSplitterPane">
                                            <TabPanel height="100%">
                                                <TabItem title="Locations" style={{ paddingTop: '4px !important' }}>
                                                    <div style={{ height: 'calc(48% - 42px)' }}>
                                                        <LocationResult data={lotLocationData} loading={lotLocationLoading} />
                                                    </div>
                                                </TabItem>
                                            </TabPanel>
                                        </div>
                                    </SplitterItem>
                                </Splitter>
                            </div>
                        </div>
                    </div>
                </TabItem>
                <TabItem title="By Sublot" style={{ paddingTop: '4px !important' }}>
                    <div className="row">
                        <div className="col-md-12 main-page">
                            <div id="accordion">
                                <SubLotFilters searchData={searchSubLotData} />
                                <Splitter orientation="vertical" className="locationMgmtSplitter">
                                    <SplitterItem resizable={true} collapsible={true} minSize="120px" size="50%">
                                        <div className="locationMgmtSplitterPane">
                                            <SubLotResult data={subLotData} onSubLotSelected={onSubLotSelected} />
                                        </div>
                                    </SplitterItem>
                                    <SplitterItem resizable={true} collapsible={true} minSize="80px">
                                        <div className="locationMgmtSplitterPane">
                                            <TabPanel height="100%">
                                                <TabItem title="Locations" style={{ paddingTop: '4px !important' }}>
                                                <div style={{ height: 'calc(48% - 42px)' }}>
                                                        <LocationResult data={subLotLocationData} loading={subLotLocationLoading} />
                                                    </div>
                                                </TabItem>
                                            </TabPanel>
                                        </div>
                                    </SplitterItem>
                                </Splitter>
                            </div>
                        </div>
                    </div>
                </TabItem>
                <TabItem title="By Location" style={{ paddingTop: '4px !important' }}>
                    <div className="row">
                        <div className="col-md-12 main-page">
                            <div id="accordion">
                                <ByLocationFilters searchData={searchByLocationData} />
                                <Splitter orientation="vertical" className="locationMgmtSplitter">
                                    <SplitterItem resizable={true} collapsible={true} minSize="120px" size="50%">
                                        <div className="locationMgmtSplitterPane">
                                            <ByLocationResult data={byLocationData} onLocationSelected={onLocationSelected} />
                                        </div>
                                    </SplitterItem>
                                    <SplitterItem resizable={true} collapsible={true} minSize="80px">
                                        <div className="locationMgmtSplitterPane">
                                            <TabPanel height="100%">
                                                <TabItem title="Inventory" style={{ paddingTop: '4px !important' }}>
                                                <div style={{ height: 'calc(48% - 42px)' }}>
                                                        <LocationInventoryResult data={locationInventoryData} />
                                                    </div>
                                                </TabItem>
                                            </TabPanel>
                                        </div>
                                    </SplitterItem>
                                </Splitter>
                            </div>
                        </div>
                    </div>
                </TabItem>
                <TabItem title="Empty Locations" style={{ paddingTop: '4px !important' }}>
                    <div className="row">
                        <div className="col-md-12 main-page">
                            <div id="accordion">
                                <EmptyLocationFilters searchData={searchEmptyLocationData} />
                                <div style={{ height: 'calc(100vh - 150px)', overflow: 'hidden' }}>
                                    <EmptyLocationResult data={emptyLocationData} />
                                </div>
                            </div>
                        </div>
                    </div>
                </TabItem>
            </TabPanel>
        </div>
    );
};
function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(LocationManagement);
